/**
 * App.jsx – Root component
 *
 * The app now serves two surfaces:
 *  - Public marketing website when opened without a Shopify host param.
 *  - Embedded Shopify admin when loaded inside the app with host present.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Provider as AppBridgeProvider,
  useAppBridge,
} from "@shopify/app-bridge-react";
import { AppProvider, Frame, Toast } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import PublicSite from "./components/PublicSite";
import Settings from "./components/Settings";

// Read Shopify params from the URL (embedded app pattern)
function getUrlParam(name) {
  return new URLSearchParams(window.location.search).get(name) || "";
}

function getShopifyApiKey() {
  return (
    window.__QUICK_URGENCY_TIMER_CONFIG__?.shopifyApiKey ||
    process.env.REACT_APP_SHOPIFY_API_KEY ||
    ""
  );
}

function EmbeddedSettings({ shop }) {
  const appBridge = useAppBridge();
  return <Settings shop={shop} appBridge={appBridge} />;
}

export default function App() {
  const host = getUrlParam("host");
  const shop = getUrlParam("shop");
  const standalone = process.env.NODE_ENV !== "production" && getUrlParam("standalone") === "1";
  const showPublicSite = !host && !standalone;
  const [billingToast, setBillingToast] = useState(null);

  // Show a toast when billing redirect returns
  const billingStatus = getUrlParam("billing");
  React.useEffect(() => {
    if (billingStatus === "success") {
      setBillingToast({ content: "🎉 Pro plan activated!", error: false });
    } else if (billingStatus === "declined") {
      setBillingToast({ content: "Billing was not approved.", error: true });
    }
  }, [billingStatus]);

  const dismissToast = useCallback(() => setBillingToast(null), []);

  useEffect(() => {
    document.body.dataset.surface = showPublicSite ? "marketing" : "app";
  }, [showPublicSite]);

  useEffect(() => {
    const loader = document.getElementById("boot-loader");
    let loaderTimeoutId = null;
    const frameId = window.requestAnimationFrame(() => {
      document.body.classList.add("app-ready");
      if (loader) {
        loaderTimeoutId = window.setTimeout(() => {
          loader.remove();
        }, 380);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (loaderTimeoutId) {
        window.clearTimeout(loaderTimeoutId);
      }
    };
  }, []);

  if (showPublicSite) {
    return <PublicSite />;
  }

  if (standalone) {
    return (
      <AppProvider i18n={enTranslations}>
        <Frame>
          <Settings shop={shop} testMode={standalone} />
        </Frame>
      </AppProvider>
    );
  }

  // App Bridge config
  const appBridgeConfig = {
    apiKey: getShopifyApiKey(),
    host,
    forceRedirect: process.env.NODE_ENV === "production",
  };

  return (
    <AppBridgeProvider config={appBridgeConfig}>
      <AppProvider i18n={enTranslations}>
        <Frame>
          {billingToast && (
            <Toast
              content={billingToast.content}
              error={billingToast.error}
              onDismiss={dismissToast}
            />
          )}
          <EmbeddedSettings shop={shop} />
        </Frame>
      </AppProvider>
    </AppBridgeProvider>
  );
}
